import { Text, UnstyledButton } from "@hackthenorth/north";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Icon } from "src/shared/components";
import globalConstants from "src/theme/globalConstants";
import {
  BLOCK_HEIGHT,
  BLOCK_WIDTH,
  HOURS_LIST,
} from "src/views/schedule/constants";
import { useScheduleContext } from "src/views/schedule/ScheduleContext";

import { TMentorShiftScheduleEvent } from "../constants";

interface ShiftProps {
  shift: TMentorShiftScheduleEvent;
  setSelectedShift: (shiftId: number) => void;
}

const Container = styled.div<{
  eventColor: string;
  blocksFromTop: number;
  numberOfBlocks: number;
}>`
  cursor: pointer;

  background: ${({ eventColor }) => eventColor};
  color: ${({ eventColor }) => (eventColor === "white" ? "black" : "white")};
  border-bottom: ${({ blocksFromTop, numberOfBlocks, eventColor }) =>
    numberOfBlocks > HOURS_LIST.length - blocksFromTop
      ? "none"
      : `2px solid ${eventColor}`};

  border-radius: ${({ blocksFromTop, numberOfBlocks }) =>
    numberOfBlocks > HOURS_LIST.length - blocksFromTop ? "5px 5px 0 0" : "5px"};

  overflow: hidden;
  overflow-y: auto;

  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  padding: 10px 12px;
  padding-top: ${({ blocksFromTop }) =>
    blocksFromTop >= 0 ? 10 : 8 + -1 * blocksFromTop * BLOCK_HEIGHT}px;

  height: 100%;
  width: 100%;

  * p {
    text-align: start;
  }

  &:focus {
    outline: none;
  }
`;

const Positioner = styled.div<{
  blocksFromTop: number;
  numberOfBlocks: number;
  overlapColumn: number;
}>`
  position: absolute;
  right: "0";
  left: ${({ overlapColumn }) => `${overlapColumn * BLOCK_WIDTH}px`};
  top: ${({ blocksFromTop }) => blocksFromTop * BLOCK_HEIGHT}px;
  height: ${({ numberOfBlocks }) => BLOCK_HEIGHT * numberOfBlocks}px;
  box-sizing: border-box;
  overflow: hidden;
  padding-right: 10px;
  width: ${BLOCK_WIDTH}px;
  z-index: 1;

  &:hover {
    z-index: 10;
    opacity: 0.9;
  }
`;

const SubContainer = styled.div`
  width: 100%;
  display: block;
`;

const NameContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 8px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 4px;
`;

const IconButton = styled(UnstyledButton)`
  margin-left: 8px;
`;

const StyledIcon = styled(Icon)`
  flex: 0 0 12px;
`;

const SmallerLineHeightText = styled(Text)`
  line-height: 18px !important;
  color: white !important;
  font-weight: 700;
`;

const TimeText = styled(Text)`
  font-size: 12px !important;
  line-height: 18px !important;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: white !important;
`;

const RegisteredContainer = styled(Text)<{ percentRegistered: number }>`
  background-color: ${({ percentRegistered }) =>
    percentRegistered === 0
      ? (globalConstants?.color?.backgroundDangerLight as string)
      : percentRegistered === 1
      ? (globalConstants?.color?.backgroundPrimaryLight as string)
      : percentRegistered < 1
      ? (globalConstants?.color?.backgroundWarningLight as string)
      : "lightgray"};
  color: ${({ percentRegistered }) =>
    percentRegistered === 0
      ? (globalConstants?.color?.textDanger as string)
      : percentRegistered === 1
      ? (globalConstants?.color?.textPrimary as string)
      : percentRegistered < 1
      ? (globalConstants?.color?.textWarning as string)
      : "gray"} !important;
  border-radius: 32px;
  display: flex;
  padding: 4px 9.189px;
  align-items: center;
  gap: -8.27px;
  font-size: 11.748px !important;
  font-style: normal;
  font-weight: 700;
`;

export const Shift: React.FC<ShiftProps> = ({ shift, setSelectedShift }) => {
  const { enableSaving, getIsSaved, toggleSaveEventWithErrorHandling } =
    useScheduleContext();

  const [registered, setRegistered] = useState<number | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [percentRegistered, setPercentRegistered] = useState(0);

  useEffect(() => {
    const registeredCount = shift.registeredMentors;
    const maxCount = shift.maxMentors;

    setRegistered(registeredCount ?? 0);
    setTotal(maxCount ?? 0);
    setPercentRegistered(maxCount > 0 ? registeredCount / maxCount : 0);
  }, [shift.registeredMentors, shift.maxMentors]);

  return (
    <>
      <Positioner
        blocksFromTop={shift.blocksFromTop}
        numberOfBlocks={shift.numberOfBlocks}
        overlapColumn={shift.overlapColumn ?? 0}
      >
        <Container
          eventColor={globalConstants?.color?.backgroundPrimary as string}
          blocksFromTop={shift.blocksFromTop}
          numberOfBlocks={shift.numberOfBlocks}
          onClick={() => setSelectedShift(shift.id)}
          role="button"
          tabIndex={0}
        >
          <SubContainer>
            <NameContainer>
              <ContentContainer>
                <SmallerLineHeightText mods="subtle">
                  {shift.title}
                </SmallerLineHeightText>
                <TimeText>
                  {shift.start.format("h:mm A") +
                    " - " +
                    shift.end.format("h:mm A")}
                </TimeText>
              </ContentContainer>
              {enableSaving && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSaveEventWithErrorHandling(shift.id);
                  }}
                >
                  <StyledIcon
                    name={getIsSaved(shift.id) ? "star" : "star-outline"}
                    fill="transparent"
                  />
                </IconButton>
              )}
            </NameContainer>
            <RegisteredContainer percentRegistered={percentRegistered}>
              {registered}/{total} Registered
            </RegisteredContainer>
          </SubContainer>
        </Container>
      </Positioner>
    </>
  );
};
